import React, { FC, ReactElement, useCallback, useMemo, useState } from "react"
import { Form, Formik } from "formik"

import { CMSService } from "../../../services/cms/cms.service"
import { enumEnCurrencyFlag, Tenant, TenantConfigData } from "../../../types/api"
import { prepareTenantSetupData } from "../../../util/api"
import { formTenantConfigData } from "../../../util/editorsForms"
import { DropdownMenu } from "../../inputs/DropdownMenu"
import { RegularFields } from "../CMSColorEditor/RegularFields"

import { ContactsFields } from "./ContactsFields"
import { FilesFields } from "./FilesFields"
import { FontConfigFields } from "./FontConfigFields"
import { SealsFields } from "./SealsFields"

const enLangFlagsOptions = Object.entries(enumEnCurrencyFlag).map((item) => ({ value: item[1], name: item[0] }))
type Props = {
  tenant: Tenant
}
export const EditorForm: FC<Props> = ({ tenant }): ReactElement => {
  const [loading, setLoading] = useState<"loading" | false>(false)

  const onSubmit = useCallback(
    async (values: TenantConfigData) => {
      setLoading("loading")

      const hasFont = values.fontConfig?.fontFamily?.trim() && values.fontConfig?.fontUrl?.trim()
      const valuesToSubmit: TenantConfigData = {
        ...values,
        fontConfig: hasFont ? { fontFamily: values.fontConfig!.fontFamily.trim(), fontUrl: values.fontConfig!.fontUrl.trim() } : undefined
      }

      const updatedData = await prepareTenantSetupData(valuesToSubmit)
      if (updatedData) {
        // Only this form's own fields. The API merges them into setup_data; sending the whole
        // object would drop every key this form does not model.
        await CMSService.updateTenantData(tenant.id, { setup_data: updatedData })
      }
      setLoading(false)
    },
    [tenant]
  )

  const initialValues = useMemo(() => formTenantConfigData(tenant), [tenant])

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ handleSubmit, handleChange, values }) => (
        <Form onSubmit={handleSubmit}>
          <FilesFields />
          <RegularFields />
          <SealsFields />
          <ContactsFields />
          <div className="mt-2">
            <DropdownMenu
              id="enCurrencyFlag"
              title="English language flag"
              value={values.enCurrencyFlag}
              options={enLangFlagsOptions}
              onChange={handleChange}
              noAllValueText
            />
          </div>

          <FontConfigFields />

          <button className={`btn btn-primary bg-transparent mt-8 ${loading || ""}`} type="submit" disabled={!!loading}>
            Save
          </button>
        </Form>
      )}
    </Formik>
  )
}
